import type { PostHog } from 'posthog-js';
import { captureCurrentUrlClickIds } from '@/lib/analytics/click-id-stash';
import { dropThirdPartyScriptExceptions } from '@/lib/analytics/third-party-exceptions';
import { getCookieValue, POSTHOG_VISITOR_ID_COOKIE } from '@/lib/posthog-identity';

// Runs before hydration and before any router navigation can rewrite the URL,
// the one moment the landing query string (?fbclid etc.) is guaranteed intact.
// Persist immediately so checkout attribution survives navigation, reloads,
// delayed purchases, and a denied/pending browser-pixel consent state.
captureCurrentUrlClickIds(true);

const posthogProjectToken = process.env.NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN;
const DEFAULT_POSTHOG_HOST = 'https://us.i.posthog.com';

// Shared, memoized posthog-js loader. It initializes only when a consent-gated
// capture/identify path calls it, keeping PostHog and its extensions out of the
// initial Lighthouse page load.
let posthogPromise: Promise<PostHog> | null = null;
let posthogInitialized = false;

export function loadPostHog(): Promise<PostHog> {
	if (!posthogProjectToken) {
		return Promise.reject(new Error('PostHog project token is not configured.'));
	}

	const token = posthogProjectToken;
	posthogPromise ??= import('posthog-js').then((module) => {
		const posthog = module.default;

		if (!posthogInitialized) {
			posthog.init(token, {
				api_host: posthogHost,
				ui_host: 'https://us.posthog.com',
				defaults: '2026-01-30',
				capture_exceptions: true,
				before_send: dropThirdPartyScriptExceptions,
				capture_pageview: false,
				autocapture: false,
				opt_out_capturing_by_default: true,
				opt_out_persistence_by_default: true,
				debug: process.env.NODE_ENV === 'development',
				...(posthogVisitorId ? { bootstrap: { distinctID: posthogVisitorId } } : {}),
			});
			posthogInitialized = true;
		}

		return posthog;
	});
	return posthogPromise;
}

export function syncPostHogConsent(posthog: PostHog, allowed: boolean) {
	if (!allowed) {
		posthog.stopSessionRecording();
		// Rotate and clear the previous analytics identity before opting out.
		// opt_out_persistence_by_default then removes SDK persistence as well.
		posthog.reset(true);
		posthog.opt_out_capturing();
		deletePostHogVisitorCookie();
		return;
	}

	if (posthog.has_opted_out_capturing()) {
		posthog.opt_in_capturing({ captureEventName: false });
	}
	posthog.startSessionRecording();
	persistPostHogVisitorCookie(posthog);
}

function persistPostHogVisitorCookie(posthog: PostHog) {
	if (typeof document === 'undefined') return;
	try {
		const distinctId = posthog.get_distinct_id();
		if (!distinctId) return;
		const secure = window.location.protocol === 'https:' ? '; Secure' : '';
		// biome-ignore lint/suspicious/noDocumentCookie: this session identity is created only after analytics consent.
		document.cookie = `${POSTHOG_VISITOR_ID_COOKIE}=${encodeURIComponent(distinctId)}; path=/; SameSite=Lax${secure}`;
	} catch {
		// Cookie persistence is optional.
	}
}

function deletePostHogVisitorCookie() {
	if (typeof document === 'undefined') return;
	const secure = window.location.protocol === 'https:' ? '; Secure' : '';
	// biome-ignore lint/suspicious/noDocumentCookie: consent withdrawal must remove the first-party analytics identity.
	document.cookie = `${POSTHOG_VISITOR_ID_COOKIE}=; path=/; max-age=0; SameSite=Lax${secure}`;
}

function resolvePostHogHost() {
	const configuredHost = process.env.NEXT_PUBLIC_POSTHOG_HOST?.trim();
	if (!configuredHost) {
		return DEFAULT_POSTHOG_HOST;
	}

	if (configuredHost.startsWith('/')) {
		return DEFAULT_POSTHOG_HOST;
	}

	try {
		const configuredUrl = new URL(configuredHost);
		const siteUrl = process.env.NEXT_PUBLIC_SITE_URL?.trim();

		if (siteUrl) {
			const siteOrigin = new URL(siteUrl).origin;
			const isSameOriginProxy =
				configuredUrl.origin === siteOrigin && configuredUrl.pathname.startsWith('/ingest');

			if (isSameOriginProxy) {
				return DEFAULT_POSTHOG_HOST;
			}
		}

		return configuredHost;
	} catch {
		return DEFAULT_POSTHOG_HOST;
	}
}

const posthogHost = resolvePostHogHost();
const posthogVisitorId =
	typeof document === 'undefined'
		? null
		: getCookieValue(document.cookie, POSTHOG_VISITOR_ID_COOKIE);
